import {
  Button,
  FormControl,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FiFileText } from 'react-icons/fi';
import { Select } from 'chakra-react-select';
import { chakraStyles } from '../common/select/styles';
import { useLazyGetSkillsChecklistDocumentQuery } from '@/app/services/nucleus';
import AlertComponent from '../common/AlertComponent';

const messageForStatus = (status) => {
  switch (status) {
    case 400:
      return 'No skills are tagged to this modality yet, so there is nothing to preview.';
    case 404:
      return 'That modality could not be found.';
    default:
      return 'Unable to generate the checklist preview. Please try again.';
  }
};

const PreviewChecklistModal = ({ isOpen, onClose, modalityOptions = [] }) => {
  const [selectedModality, setSelectedModality] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const [triggerPreview, { isFetching }] = useLazyGetSkillsChecklistDocumentQuery();

  useEffect(() => {
    if (!isOpen) {
      setSelectedModality(null);
      setErrorMessage('');
    }
  }, [isOpen]);

  const handlePreview = async () => {
    if (!selectedModality) return;
    setErrorMessage('');
    try {
      const blob = await triggerPreview(selectedModality.value).unwrap();
      // The browser releases the object URL on tab close / page unload, so no manual revoke is needed.
      window.open(URL.createObjectURL(blob), '_blank');
      onClose();
    } catch (e) {
      setErrorMessage(messageForStatus(e?.status));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <FiFileText />
            <Text>Preview Checklist Document</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={4} align="stretch">
            {errorMessage && <AlertComponent status="warning" title="Preview unavailable" description={errorMessage} />}
            <Text fontSize="sm" color="gray.600">
              Select a modality to preview its rendered skills-checklist PDF.
            </Text>
            <FormControl aria-label="preview-modality-control">
              <Select
                aria-label="preview-modality"
                chakraStyles={chakraStyles}
                focusBorderColor="brand.700"
                options={modalityOptions}
                value={selectedModality}
                onChange={(opt) => {
                  setSelectedModality(opt);
                  setErrorMessage('');
                }}
                isClearable
                isSearchable
                placeholder="Select a modality"
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack justifyContent="end" width="100%">
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="purple" onClick={handlePreview} isDisabled={!selectedModality} isLoading={isFetching}>
              Preview
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

PreviewChecklistModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  modalityOptions: PropTypes.array,
};

export default PreviewChecklistModal;
